import { fetchWithAuthV2 } from '../../utils/requests';

export type UploadConfiguration = {
  disruption_grace_period_seconds: number;
  active_retry_window_seconds: number;
  resumable_upload_expiry_minutes: number;
  resumable_cancellation_buffer_minutes: number;
};

export const DEFAULT_UPLOAD_CONFIGURATION: UploadConfiguration = {
  disruption_grace_period_seconds: 30,
  active_retry_window_seconds: 15 * 60,
  resumable_upload_expiry_minutes: 120,
  resumable_cancellation_buffer_minutes: 60,
};

export const UPLOAD_CONFIGURATION_QUERY_KEY = ['upload-configuration'] as const;

export const uploadConfigurationReadPath = (organizationId: number) =>
  `/api/v1/organizations/${organizationId}/upload_configuration`;
export const UPLOAD_CONFIGURATION_ADMIN_PATH =
  '/api/v1/organizations/1/dashboard/upload_configuration';

const normalizePositiveInteger = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0
    ? value
    : fallback;

export function normalizeUploadConfiguration(
  raw: Partial<UploadConfiguration> | null | undefined
): UploadConfiguration {
  return {
    disruption_grace_period_seconds: normalizePositiveInteger(
      raw?.disruption_grace_period_seconds,
      DEFAULT_UPLOAD_CONFIGURATION.disruption_grace_period_seconds
    ),
    active_retry_window_seconds: normalizePositiveInteger(
      raw?.active_retry_window_seconds,
      DEFAULT_UPLOAD_CONFIGURATION.active_retry_window_seconds
    ),
    resumable_upload_expiry_minutes: normalizePositiveInteger(
      raw?.resumable_upload_expiry_minutes,
      DEFAULT_UPLOAD_CONFIGURATION.resumable_upload_expiry_minutes
    ),
    resumable_cancellation_buffer_minutes: normalizePositiveInteger(
      raw?.resumable_cancellation_buffer_minutes,
      DEFAULT_UPLOAD_CONFIGURATION.resumable_cancellation_buffer_minutes
    ),
  };
}

export async function fetchUploadConfiguration(
  organizationId: number
): Promise<UploadConfiguration> {
  const response = (await fetchWithAuthV2(
    uploadConfigurationReadPath(organizationId),
    {
      method: 'GET',
      fallbackErrorMessage: 'Failed to load upload configuration',
    }
  )) as Partial<UploadConfiguration>;

  return normalizeUploadConfiguration(response);
}

export async function saveUploadConfiguration(
  configuration: UploadConfiguration
): Promise<UploadConfiguration> {
  const response = (await fetchWithAuthV2(UPLOAD_CONFIGURATION_ADMIN_PATH, {
    method: 'PATCH',
    fallbackErrorMessage: 'Failed to save upload configuration',
    body: JSON.stringify(configuration),
  })) as Partial<UploadConfiguration>;

  return normalizeUploadConfiguration(response);
}
